import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map } from "rxjs/operators";
import { ApiBaseResponse } from "../shared/interfaces/api-base-response";
import { API_ENDPOINTS, API_URL } from "../shared/interfaces/global";
import { SettingsDTO } from "../shared/interfaces/RegisteredTenantsListDto";
import { TenantUsageDetails, TenantUsageResponse, TenantUsageSummary } from "../shared/interfaces/tenant-usage.interface";
import { ActiveUsersSeriesRequest, FeatureRuleText } from "../shared/interfaces/usage.interface";
import { adoptionRuleText, AdoptionRuleText } from "../shared/Utils/feature-stage";
import { TtlCache } from "../shared/Utils/ttl-cache";
import { ApiHandlerService } from "./api-handler.service";

@Injectable({
  providedIn: "root",
})
export class TenantsService extends ApiHandlerService {
  
  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  private readonly cache = new TtlCache<any>();
  /** Stored totals only change with the nightly rollup, so a session keeps every answer it has seen */
  private readonly sessionCache = new TtlCache<any>(Number.POSITIVE_INFINITY);

  getCachedTenants(): any[] | null {
    return this.cache.get("tenants")?.Response?.Tenants || null;
  }

  getTenantsList(forceRefresh = false) {
    return this.cache.getOrLoad("tenants", () => this.GetAll(API_URL + API_ENDPOINTS.tenants).toPromise<any>(), forceRefresh);
  }

  getAllLicenseUsers() {
    return this.Post(API_URL + API_ENDPOINTS.searchUsers, {
      FilterGroups: [],
      Properties: [],
      Limit: 50000
    }).toPromise<any>();
  }

  getAllPortalUsers() {
    return this.cache.getOrLoad("portalUsers", () => this.GetAll(API_URL + API_ENDPOINTS.allPortalUsers).toPromise<any>());
  }
  
  /** refresh recomputes the headline cards server-side instead of serving the briefly cached copy */
  getTenantUsageSummary(companyId: string, refresh = false): Promise<TenantUsageResponse<TenantUsageSummary>> {
    return this.Get(API_URL + API_ENDPOINTS.getTenantUsageSummary + "?companyId=" + companyId + (refresh ? "&refresh=true" : "")).toPromise<any>();
  }

  /** refresh recomputes the details server-side instead of serving the briefly cached copy */
  getTenantUsageDetails(companyId: string, refresh = false): Promise<TenantUsageResponse<TenantUsageDetails>> {
    return this.Get(API_URL + API_ENDPOINTS.getTenantUsageDetails + "?companyId=" + companyId + (refresh ? "&refresh=true" : "")).toPromise<any>();
  }

  getTenantUsageTrends(companyId: string, days: number = 365) {
    return this.Get(API_URL + API_ENDPOINTS.getTenantUsageTrends + "?companyId=" + companyId + "&days=" + days).toPromise<any>();
  }

  getTenantUsageMetricDetail(companyId: string, metric: string) {
    return this.Get(API_URL + API_ENDPOINTS.getTenantUsageMetricDetail + "?companyId=" + companyId + "&metric=" + metric).toPromise<any>();
  }

  getTenantUserActivityDetail(companyId: string, userId: string, days: number = 30) {
    // tz offset lets the backend bucket daily activity into the viewer's local days
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    return this.Get(API_URL + API_ENDPOINTS.getTenantUserActivityDetail + "?companyId=" + companyId + "&userId=" + userId + "&days=" + days + "&tzOffsetMinutes=" + tzOffsetMinutes).toPromise<any>();
  }

  getFleetUsageOverview() {
    return this.Get(API_URL + API_ENDPOINTS.getFleetUsageOverview).toPromise<any>();
  }

  /** Never rejects: a failed lookup yields the number-free rule text so a page is never blocked or shown wrong thresholds */
  getAdoptionRuleText(): Promise<AdoptionRuleText> {
    return this.cache.getOrLoad("adoptionThresholds", () => this.Get(API_URL + API_ENDPOINTS.getFeatureAdoptionThresholds).toPromise<any>())
      .then((res: any) => adoptionRuleText(res?.Response))
      .catch(() => adoptionRuleText(null));
  }

  /** Per-feature using and adopted wording for the fleet scorecard */
  getFeatureRules(): Promise<FeatureRuleText[]> {
    return this.cache.getOrLoad("adoptionRules", () => this.Get(API_URL + API_ENDPOINTS.getFeatureAdoptionRules).toPromise<any>()
      .then((res: any) => {
        if (!Array.isArray(res?.Response)) {
          throw new Error("GetFeatureAdoptionRules did not return a feature list");
        }
        return res.Response as FeatureRuleText[];
      }));
  }

  getFleetFeatureAdoptionHistory(months: number = 12) {
    return this.cache.getOrLoad("adoptionHistory:" + months,
      () => this.Get(API_URL + API_ENDPOINTS.getFleetFeatureAdoptionHistory + "?months=" + months).toPromise<any>());
  }

  /** Only complete answers are kept: a failure, or history still being prepared, is asked for again next time */
  getActiveUsersSeries(request: ActiveUsersSeriesRequest) {
    const key = ["activeUsers", request.Granularity, request.MonthsBack ?? "all", [...request.CompanyIds].sort().join(",")].join("|");
    const cached = this.sessionCache.get(key);
    if (cached !== undefined) {
      return Promise.resolve(cached);
    }
    return this.Post(API_URL + API_ENDPOINTS.getActiveUsersSeries, request).toPromise<any>()
      .then((res: any) => {
        if (!res?.IsSuccessful || !res?.Response) {
          throw new Error("Active users request failed");
        }
        return res.Response.PendingCompanies > 0 ? res : this.sessionCache.set(key, res);
      });
  }

  runUsageMetricsRollup(companyId?: string, forceBackfill: boolean = false) {
    const params: string[] = [];
    if (companyId) params.push("companyId=" + companyId);
    if (forceBackfill) params.push("forceBackfill=true");
    const query = params.length ? "?" + params.join("&") : "";
    return this.Post(API_URL + API_ENDPOINTS.runUsageMetricsRollup + query, {}).toPromise<any>();
  }

  getUsageRollupStatus() {
    return this.Get(API_URL + API_ENDPOINTS.getUsageRollupStatus).toPromise<any>();
  }

  getTenantsTasks() {
    return (
      this.GetTenantData(API_URL+API_ENDPOINTS.Taskslist)
        // .pipe(map((x: ApiBaseResponse) => x.Response))
        .toPromise<any>()
    );
  }

  getTenantsProjects() {
    return (
      this.GetTenantData(API_URL+API_ENDPOINTS.ProjectList)
        // .pipe(map((x: ApiBaseResponse) => x.Response))
        .toPromise<any>()
    );
  }
  getTenantsEstimates() {
    return (
      this.GetTenantData(API_URL+API_ENDPOINTS.EstimateList)
        // .pipe(map((x: ApiBaseResponse) => x.Response))
        .toPromise<any>()
    );
  }
  getTenantsCustomers() {
    return (
      this.GetTenantData(API_URL+API_ENDPOINTS.CustomerList)
        // .pipe(map((x: ApiBaseResponse) => x.Response))
        .toPromise<any>()
    );
  }
  getTenantsContractor() {
    return (
      this.GetTenantData(API_URL+API_ENDPOINTS.ContractorList)
        // .pipe(map((x: ApiBaseResponse) => x.Response))
        .toPromise<any>()
    );
  }
  getTenantsFile() {
    return (
      this.GetTenantData(API_URL+API_ENDPOINTS.FileList)
        // .pipe(map((x: ApiBaseResponse) => x.Response))
        .toPromise<any>()
    );
  }
  getTenantsInvoice() {
    return (
      this.GetTenantData(API_URL+API_ENDPOINTS.InvoiceList)
        // .pipe(map((x: ApiBaseResponse) => x.Response))
        .toPromise<any>()
    );
  }
  getTenantsEmployee() {
    return (
      this.GetTenantData(API_URL+API_ENDPOINTS.EmployeeList)
        // .pipe(map((x: ApiBaseResponse) => x.Response))
        .toPromise<any>()
    );
  }

  UnlockInvoiceStatus(companyId: any, invoiceId: any){
    return this.Get(API_URL + API_ENDPOINTS.UnlockInvoiceStatus+`?companyId=${companyId}`+`&invoiceId=${invoiceId}`)
    .toPromise<any>();
  }
  
  DowngradeUser(companyId: any, number: any){
    
    return this.Get(API_URL + API_ENDPOINTS.DowngradeUser+`?companyId=${companyId}`+`&number=${number}`)
    .toPromise<any>();
  }
  
  ExtendTrial(companyId: any, date: any){

    return this.Get(API_URL + API_ENDPOINTS.ExtendTrial+`?companyId=${companyId}`+`&date=${date}`)
    .toPromise<any>();
  }

  ConvertTrialToSubscription(data: any) {
    return this.Post(API_URL + API_ENDPOINTS.ConvertTrialToSubscription, data)
      .toPromise<any>();
  }

  CheckStripeSubscriptionExists(stripeSubscriptionId: string) {
    return this.Get(API_URL + API_ENDPOINTS.CheckStripeSubscriptionExists + `?stripeSubscriptionId=${stripeSubscriptionId}`)
      .toPromise<any>();
  }

  ArchiveEmployee(companyId: any, Id: number ,archive: boolean){
    return this.Post(API_URL + API_ENDPOINTS.ArchiveEmployee+`?companyId=${companyId}`+`&Id=${Id}`+`&archive=${archive}`,{})
    .toPromise<any>();
  }
  tradesList(companyId: any){
    return this.Get(API_URL + API_ENDPOINTS.tradesList+`?companyId=${companyId}`)
    .toPromise<any>();
  }

  ChangeInvoiceStatus(companyId: any, invoiceId: any, Status: any){
    return this.Get(API_URL + API_ENDPOINTS.changeInvoiceStatus+`?companyId=${companyId}`+`&invoiceId=${invoiceId}`+`&Status=${Status}`)
    .toPromise<any>();
  }

  RemoveSignatureFromEstimate(companyId: any, estimateId: any, detailId: any){
    return this.Get(API_URL + API_ENDPOINTS.RemoveSignatureFromEstimate+`?companyId=${companyId}`+`&estimateId=${estimateId}`+`&detailId=${detailId}`)
    .toPromise<any>();
  }
  
  RemoveInvoicesFromEstimate(companyId: any, estimateId: any){
    return this.Get(API_URL + API_ENDPOINTS.RemoveInvoicesFromEstimate+`?companyId=${companyId}`+`&estimateId=${estimateId}`)
    .toPromise<any>();
  }


  ImpersonateUser(userId: string) {
    return this.Post(API_URL + API_ENDPOINTS.ImpersonateUser+`?userId=${userId}`,{})
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise<any>();
  }
  AddUpdateEmployee(EmployeeDTO: any,companyId: any,) {
    return this.Post(API_URL + API_ENDPOINTS.AddUpdateEmployee +`?companyId=${companyId}`, EmployeeDTO)
      .toPromise<any>();
  }

  SaveTenantSettings(settings:any){
    return this.Post(API_URL + API_ENDPOINTS.saveTenantSettings , settings).toPromise();
  }

  async getTenantSettingList(companyId:string){
    return (await this.GetAll(
         `${API_URL + API_ENDPOINTS.getPortalSetting}?companyId=${companyId}` 
    )).toPromise();
  }


  enableDisableTenant(id: string, isDisabled: boolean) {
    return this.Post(API_URL + API_ENDPOINTS.enableDisableCompany + `?Id=${id}&IsDisabled=${isDisabled}`, {}).toPromise();
  }

  setTestAccount(id: string, isTestAccount: boolean) {
    return this.Post(API_URL + API_ENDPOINTS.setTestAccount + `?Id=${id}&IsTestAccount=${isTestAccount}`, {}).toPromise();
  }

  getTenantDetails(id: string) {
    return this.Get(API_URL + API_ENDPOINTS.tenantDetails + `?CompanyId=${id}`)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise<any>();
  }

  getDashboardData() {
    return this.Get(API_URL + API_ENDPOINTS.dashboardData)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  getCompanyStripeCoupon(companyId: string) {
    return this.Get(API_URL + API_ENDPOINTS.CompanyStripeCoupon + `?companyId=${companyId}`)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  killQBJobs(companyId: string) {
    return this.Post(API_URL + API_ENDPOINTS.killQBJobs + `?companyId=${companyId}`, {}).toPromise();
  }

  saveCompanyStripeCoupon(companyId: string, coupon: string) {
    return this.Post(API_URL + API_ENDPOINTS.SaveCompanyStripeCoupon + `?companyId=${companyId}&coupon=${coupon}`, {}).toPromise();
  }

  getTenant(companyId: string) {
    return this.Get(API_URL + API_ENDPOINTS.GetTenant + `?CompanyId=${companyId}`).toPromise();
  }

  getPricingChangeAcknowledgement(companyId: string, year: number) {
    return this.Get(API_URL + API_ENDPOINTS.PricingChangeAcknowledgement + `?companyId=${companyId}` + '&year=' + year)
    .pipe(map((x: ApiBaseResponse) => x.Response))
    .toPromise();
  }

  getDeletedItems(filter: any) {
    return this.Post(API_URL + API_ENDPOINTS.deletedItems, filter)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  getDeletedInvoicesForEstimate(companyId: string, estimateId: string) {
    return this.Post(API_URL + API_ENDPOINTS.getDeletedInvoicesForEstimate, {
      companyId: companyId,
      estimateId: estimateId
    })
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  restoreDeletedItem(request: { companyId: string; entityId: string; entityType: string; invoiceIdsToRestore?: string[]; childIdsToRestore?: string[] }) {
    return this.Post(API_URL + API_ENDPOINTS.restoreDeletedItem, request)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  bulkRestoreDeletedItems(request: { companyId: string; items: { entityId: string; entityType: string; childIdsToRestore?: string[] }[] }) {
    return this.Post(API_URL + API_ENDPOINTS.bulkRestoreDeletedItems, request)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  getRestoreContext(companyId: string, entityId: string, entityType: string) {
    return this.Post(API_URL + API_ENDPOINTS.getRestoreContext, {
      companyId,
      entityId,
      entityType
    })
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  getEstimateHistory(request: { companyId: string; estimateId: string; estimateDetailId?: string | null; pageNumber?: number; pageSize?: number }) {
    return this.Post(API_URL + API_ENDPOINTS.estimateHistory, request)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  restoreEstimate(request: { companyId: string; estimateId: string; estimateDetailId?: string | null; snapshotDate: string; templateName?: string; mode?: number; dryRun?: boolean }) {
    return this.Post(API_URL + API_ENDPOINTS.restoreEstimate, request)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }
  
  SendPasswordReset(userId: string) {
    return this.Post(API_URL + API_ENDPOINTS.sendPasswordReset + `?userId=${userId}`, {})
      .toPromise<any>();
  }

  SetEmployeePassword(userId: string, password: string) {
    return this.Post(API_URL + API_ENDPOINTS.setEmployeePassword, { userId, password })
      .toPromise<any>();
  }

  ForceRefreshUser(companyId: any, userId: string) {
    return this.Post(API_URL + API_ENDPOINTS.forceRefreshUser + `?companyId=${companyId}&userId=${userId}`, {})
      .toPromise<any>();
  }

  bulkDeleteCustomers(companyId: string, customerIds: string[]) {
    return this.Post(API_URL + API_ENDPOINTS.bulkDeleteCustomers, {
      companyId: companyId,
      customerIds: customerIds
    }).toPromise();
  }

  getCustomerDeletionPreview(companyId: string, customerIds: string[]) {
    return this.Post(API_URL + API_ENDPOINTS.getCustomerDeletionPreview, {
      companyId: companyId,
      customerIds: customerIds
    }).toPromise();
  }

  // Cost Item methods
  getCostItems(companyId: string) {
    return this.Get(API_URL + API_ENDPOINTS.getCostItems + `?companyId=${companyId}`)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  bulkDeleteCostItems(companyId: string, costItemIds: string[]) {
    return this.Post(API_URL + API_ENDPOINTS.bulkDeleteCostItems, {
      companyId: companyId,
      costItemIds: costItemIds
    }).toPromise();
  }

  getCostItemDeletionPreview(companyId: string, costItemIds: string[]) {
    return this.Post(API_URL + API_ENDPOINTS.getCostItemDeletionPreview, {
      companyId: companyId,
      costItemIds: costItemIds
    }).toPromise();
  }

  listEstimateTemplates(companyId: string) {
    return this.Get(API_URL + API_ENDPOINTS.listEstimateTemplates + `?companyId=${companyId}`)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  exportEstimateTemplates(companyId: string, companyName: string) {
    return this.Get(API_URL + API_ENDPOINTS.exportEstimateTemplates + `?companyId=${companyId}&companyName=${encodeURIComponent(companyName)}`)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  deleteEstimateTemplate(companyId: string, templateId: string) {
    return this.Post(API_URL + API_ENDPOINTS.deleteEstimateTemplate, {
      companyId: companyId,
      templateId: templateId
    }).toPromise<any>();
  }

  importEstimateTemplates(companyId: string, exportData: any, duplicateHandling: string = 'rename') {
    return this.Post(API_URL + API_ENDPOINTS.importEstimateTemplates, {
      companyId: companyId,
      exportData: exportData,
      duplicateHandling: duplicateHandling
    })
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  listScheduleTemplates(companyId: string) {
    return this.Get(API_URL + API_ENDPOINTS.listScheduleTemplates + `?companyId=${companyId}`)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  exportScheduleTemplates(companyId: string, companyName: string) {
    return this.Get(API_URL + API_ENDPOINTS.exportScheduleTemplates + `?companyId=${companyId}&companyName=${encodeURIComponent(companyName)}`)
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }

  deleteScheduleTemplate(companyId: string, templateId: string) {
    return this.Post(API_URL + API_ENDPOINTS.deleteScheduleTemplate, {
      companyId: companyId,
      templateId: templateId
    }).toPromise<any>();
  }

  importScheduleTemplates(companyId: string, exportData: any, duplicateHandling: string = 'rename') {
    return this.Post(API_URL + API_ENDPOINTS.importScheduleTemplates, {
      companyId: companyId,
      exportData: exportData,
      duplicateHandling: duplicateHandling
    })
      .pipe(map((x: ApiBaseResponse) => x.Response))
      .toPromise();
  }
}
